import { type Dispatch, type SetStateAction, useContext, useState } from 'react'

import BrokerContext from 'components/context/BrokerContext'
import { Button } from 'slab'
import { type BrokersBroker } from 'utils/web_api.schemas'

import { type EmployerFilters } from './EmployerReviewsTable'

type QuickFilterName =
  | 'pending'
  | 'bangor'
  | 'failing_with_confirmed_enrollments'
  | 'failing_with_sent_invites'
  | 'assigned_to_me'
  | null

type EmployerReviewsQuickFiltersProps = {
  filters: EmployerFilters
  setFilters: Dispatch<SetStateAction<EmployerFilters>>
}

const EmployerReviewsQuickFilters = ({
  setFilters,
}: EmployerReviewsQuickFiltersProps) => {
  const { currentBroker } = useContext(BrokerContext)
  const [activeFilter, setActiveFilter] = useState<QuickFilterName>(null)

  const setPendingFilters = () => {
    setFilters(() => ({
      review_statuses: ['pending'],
    }))
    setActiveFilter('pending')
  }

  const setBangorFilters = () => {
    setFilters(() => ({
      bank_partners: ['Bangor'],
    }))
    setActiveFilter('bangor')
  }

  const setFailingWithConfirmedEnrollmentsFilters = () => {
    setFilters(() => ({
      bank_onboarding_status: 'failing',
      onboarding_status: ['completed'],
      invited_employees: 'true',
      has_confirmed_enrollments: 'true',
      review_statuses: ['pending'],
    }))
    setActiveFilter('failing_with_confirmed_enrollments')
  }

  const setFailingWithSentInvitesFilters = () => {
    setFilters(() => ({
      bank_onboarding_status: 'failing',
      invited_employees: 'true',
      review_statuses: ['pending'],
    }))
    setActiveFilter('failing_with_sent_invites')
  }

  const setAssignedToMeFilters = (
    setFilters: Dispatch<SetStateAction<EmployerFilters>>,
    currentBroker: BrokersBroker | undefined
  ) => {
    setFilters(() => ({
      broker_ids: currentBroker ? [currentBroker.id] : [],
    }))
    setActiveFilter('assigned_to_me')
  }

  return (
    <div className='ml-1 flex items-center justify-start gap-2 flex-shrink-0 flex-wrap'>
      <div className='text-sm font-semibold min-w-16'>Quick filters</div>
      <Button
        variant={activeFilter === 'pending' ? 'tertiary' : 'tertiary-deemphasized'}
        onClick={setPendingFilters}
      >
        Waiting for Review
      </Button>
      <Button
        variant={
          activeFilter === 'failing_with_confirmed_enrollments'
            ? 'tertiary'
            : 'tertiary-deemphasized'
        }
        onClick={setFailingWithConfirmedEnrollmentsFilters}
      >
        Failing with Confirmed Enrollments
      </Button>
      <Button
        variant={
          activeFilter === 'failing_with_sent_invites'
            ? 'tertiary'
            : 'tertiary-deemphasized'
        }
        onClick={setFailingWithSentInvitesFilters}
      >
        Failing with Sent Invites
      </Button>
      <Button
        variant={activeFilter === 'bangor' ? 'tertiary' : 'tertiary-deemphasized'}
        onClick={setBangorFilters}
      >
        Onboarding to Bangor
      </Button>
      <Button
        variant={activeFilter === 'assigned_to_me' ? 'tertiary' : 'tertiary-deemphasized'}
        onClick={() => setAssignedToMeFilters(setFilters, currentBroker)}
      >
        Assigned To Me
      </Button>
    </div>
  )
}

export default EmployerReviewsQuickFilters
